import { format } from 'date-fns-tz';
import { t } from 'i18next';

import type { RewarderPromoCode } from '~/api/ReferralProgram';
import { company } from '~/company/Company';
import { PromocodesTab } from '~/stores/PromotionsStore/interfaces';
import {
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';

import { InactiveReason, PromocodeType } from './Promocode.types';

interface PromocodeDependencies {
  id: number;
  status: PromocodesTab;
  type: PromocodeType;
  code: string;
  value: number;
  minimumPurchase: number;
  deletedAt: string | null;
  usageLimitAll: number;
  usageLimitCustomer: number;
  usageCurrent: number;
  usageCurrentCustomer: number;
  orderNumberRange: [number, number] | null;
  dateRange: [string, string];
  allowedCategoriesData?: PromocodeCategory[];
  order?: PromocodeOrderDependencies;
  isNew?: boolean;
}

interface PromocodeCategory {
  id: number;
  name: string;
  slug: string;
}

interface PromocodeOrderDependencies {
  id: string;
  order_id: string;
  created_at: string;
}

interface PromocodeOrder extends Pick<PromocodeOrderDependencies, 'id'> {
  orderId: string;
  createdAt: Date;
}

export class Promocode {
  private readonly _type: PromocodeType;
  private readonly _value: number;

  // The minimum purchase amount required to use the promocode
  private readonly _minimumPurchase: number;
  // The date the promocode was deleted
  private readonly _deletedAt: Date | null;

  // How many times the promocode can be used by all customers
  private readonly _usageLimitAll: number;
  // How many times the promocode has been used by all customers
  private readonly _usageCurrent: number;

  // How many times the promocode can be used by the current customer
  private readonly _usageLimitCustomer: number;
  // How many times the promocode has been used by the current customer
  private readonly _usageCurrentCustomer: number;

  private readonly _orderNumberRange: [number, number] | null;
  private readonly _dateRange: [Date, Date];

  public readonly id: string;
  public readonly status: PromocodesTab;
  public readonly code: string;
  public readonly allowedCategories: PromocodeCategory[];
  public readonly order?: PromocodeOrder;
  public readonly isNew: boolean;

  constructor({
    id,
    status,
    type,
    code,
    value,
    minimumPurchase,
    deletedAt,
    usageLimitAll,
    usageLimitCustomer,
    usageCurrent,
    usageCurrentCustomer,
    orderNumberRange,
    dateRange,
    allowedCategoriesData = [],
    order,
    isNew = false,
  }: PromocodeDependencies) {
    this._type = type;
    this._value = value;
    this._minimumPurchase = minimumPurchase;
    this._deletedAt = deletedAt ? new Date(deletedAt) : null;
    this._usageLimitAll = usageLimitAll;
    this._usageCurrent = usageCurrent;
    this._orderNumberRange = orderNumberRange;
    this._dateRange = dateRange.map((date) => new Date(date)) as [Date, Date];

    this.status = status;
    this.code = code;
    this._usageLimitCustomer = usageLimitCustomer;
    this._usageCurrentCustomer = usageCurrentCustomer;
    this.allowedCategories = allowedCategoriesData;
    this.isNew = isNew;

    if (order) {
      this.id = `${id}-${order.id}-${order.order_id}`;
      this.order = {
        ...order,
        orderId: order.order_id,
        createdAt: new Date(order.created_at),
      };
    } else {
      this.id = `${id}`;
    }
  }

  // Sorting promocodes
  public static sort(a: Promocode, b: Promocode): number {
    // First by priority (welcome code is always higher)
    if (b._priority !== a._priority) {
      return b._priority - a._priority;
    }

    return 0;
  }

  static fromRewarderPromoCode(item: RewarderPromoCode): Promocode {
    return new Promocode({
      id: item.id,
      status: PromocodesTab.AVAILABLE,
      type: item.type as PromocodeType,
      code: item.code,
      value: item.value,
      minimumPurchase: item.minimumPurchase,
      deletedAt: null,
      usageLimitAll: item.usageLimitAll,
      usageLimitCustomer: item.usageLimitCustomer,
      usageCurrent: item.usageCurrent,
      usageCurrentCustomer: 0,
      orderNumberRange: item.orderNumberRange,
      dateRange: item.dateRange,
      isNew: item.is_new,
    });
  }

  private get _isWelcome(): boolean {
    if (!this._orderNumberRange) {
      return false;
    }

    const [startOrder, endOrder] = this._orderNumberRange;

    return (
      (startOrder === 0 && endOrder === 1) ||
      (startOrder === 1 && endOrder === 1)
    );
  }

  /**
   * The priority of the promocode
   * 1 - Welcome promo
   * 0 - Other promos
   */
  private get _priority(): number {
    if (this._isWelcome) {
      return 1;
    }

    return 0;
  }

  private get _expiresAt(): Date {
    return this._dateRange[1];
  }

  public get isActive(): boolean {
    return this.status === PromocodesTab.AVAILABLE;
  }

  public get isInactive(): boolean {
    return this.status === PromocodesTab.INACTIVE;
  }

  public get value(): string {
    return this._type === PromocodeType.PERCENTAGE
      ? `${this._value}%`
      : formatPriceWithCurrency(convertPenceToPounds(this._value));
  }

  public get minimumPurchase(): string {
    return formatPriceWithCurrency(convertPenceToPounds(this._minimumPurchase));
  }

  public get title(): string {
    return t('promocodeTitle', { value: this.value });
  }

  public get description(): string | null {
    // Сначала проверяем, является ли промокод Welcome
    if (this._isWelcome) {
      return t('promocodeDescription.welcome', { value: this.value });
    }

    if (
      !this._orderNumberRange ||
      !this._orderNumberRange[0] ||
      !this._orderNumberRange[1]
    ) {
      if (this._minimumPurchase) {
        return t('promocodeDescription.purchaseFromToGet', {
          minimumPurchase: this.minimumPurchase,
          value: this.value,
        });
      }

      return null;
    }

    const [startOrder, endOrder] = this._orderNumberRange;

    // Single order number
    if (startOrder === endOrder) {
      return this._minimumPurchase
        ? t('promocodeDescription.purchaseFromForOrder', {
            minimumPurchase: this.minimumPurchase,
            orderNumber: startOrder,
          })
        : t('promocodeDescription.getForOrder', {
            value: this.value,
            orderNumber: startOrder,
          });
    }

    // Order range
    return this._minimumPurchase
      ? t('promocodeDescription.purchaseFromForOrders', {
          minimumPurchase: this.minimumPurchase,
          startOrder,
          endOrder,
        })
      : t('promocodeDescription.getForOrders', {
          value: this.value,
          startOrder,
          endOrder,
        });
  }

  public get usageLimitCustomerDescription(): string | null {
    if (this._isWelcome) {
      return null;
    }

    if (!this.isActive) {
      return null;
    }

    if (
      !this._usageLimitCustomer ||
      this._usageCurrentCustomer >= this._usageLimitCustomer
    ) {
      return null;
    }

    return t('promocodeUsageLimitCustomer', {
      usageLimitCustomer: this._usageLimitCustomer,
      current: this._usageLimitCustomer - this._usageCurrentCustomer,
    });
  }

  public get inactiveReason(): string | null {
    if (this._deletedAt !== null) {
      return t(`inactiveReason.${InactiveReason.REMOVED}`);
    }

    if (this._usageCurrent >= this._usageLimitAll) {
      return t(`inactiveReason.${InactiveReason.LIMIT_REACHED}`);
    }

    if (new Date() > this._expiresAt) {
      return t(`inactiveReason.${InactiveReason.EXPIRED}`);
    }

    return null;
  }

  public get titleWithOrder(): string | null {
    return this.order
      ? t('promocodeTitleWithOrder', { orderId: this.order.orderId })
      : null;
  }

  public getDateWithOrder(locale: Locale): string | null {
    return this.order
      ? format(new Date(this.order.createdAt), 'MMMM d, yyyy', {
          timeZone: company.config.timeZone,
          locale,
        })
      : null;
  }

  public getValidUntil(locale: Locale): string | null {
    if (this._isWelcome) {
      return null;
    }

    const now = new Date();
    const monthInMs = 30 * 24 * 60 * 60 * 1000;

    if (this._expiresAt.getTime() - now.getTime() <= monthInMs) {
      return null;
    }

    return t('promocodeUntil', {
      date: format(this._expiresAt, 'MMM d', {
        timeZone: company.config.timeZone,
        locale,
      }),
    });
  }
}
